import { useMemo } from "react";
import { View, Flex, Text } from "@adobe/react-spectrum";
import {
  formatTime,
  getCallbackIcon,
  getInitialsAndColor,
} from "../../../utils";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { MissedCallIcon } from "../Icons";
import { SpeedDialData } from "../../../types/generic.types";

interface CVHistorySidebarProps {
  activeCallerId: string;
  getActiveCallId: (id: string) => void;
  callList: any[];
  type: "callback" | "voicemail";
}

const CVHistorySidebar = ({
  activeCallerId,
  getActiveCallId,
  callList,
  type,
}: CVHistorySidebarProps) => {
  const common = useSelector((state: RootState) => state.common);
  const speedDial = useSelector((state: RootState) => state.history.speedDials);

  const computedCallList = useMemo(() => {
    return callList?.map((call) => {
      let firstName = "";
      let lastName = "";

      // Try to get matching SpeedDial entry by phone number
      const phoneNumber = call?.customer?.attributes?.PHONE_NUMBER;
      const speedDialEntry = speedDial?.find(
        (entry: SpeedDialData) => entry?.phoneNumber === phoneNumber
      );

      if (speedDialEntry) {
        firstName = speedDialEntry.firstName || "N";
        lastName = speedDialEntry.lastName || "A";
      } else {
        firstName = call?.customer?.attributes?.FIRSTNAME || "N";
        lastName = call?.customer?.attributes?.LASTNAME || "A";
      }

      const initialsData = getInitialsAndColor(
        `${firstName[0]} ${lastName[0]}`
      );

      return {
        ...call,
        initialsData,
        displayName: speedDialEntry
          ? `${firstName} ${lastName}`
          : `${call?.customer?.attributes?.FIRSTNAME ?? ""} ${
              call?.customer?.attributes?.LASTNAME ?? ""
            }`.trim() ||
            call?.customer?.attributes?.PHONE_NUMBER ||
            "-",
      };
    });
  }, [callList, type, speedDial]);

  return (
    <View
      UNSAFE_className="call-history-sidebar"
      borderTopColor={"gray-200"}
      borderTopWidth={"thick"}
      height={"100%"}
    >
      {computedCallList?.length > 0 &&
        computedCallList.map((call) => {
          const { connectedTime, endTime, missed } = call?.self || {};
          const { formattedConnectedTime, formattedDifference } = formatTime(
            connectedTime,
            endTime
          );
          const { initials, backgroundColor } = call.initialsData;

          return (
            <div
              key={call.id}
              onClick={() => getActiveCallId(call?.self?.id)}
              className="call-history-sidebar-list"
            >
              <Flex
                flexGrow={1}
                justifyContent={"space-between"}
                direction={"column"}
                UNSAFE_className={`call-history-sidebar-list-item ${
                  call?.self?.id === activeCallerId
                    ? " call-history-sidebar-list-active"
                    : ""
                }`}
              >
                <Flex>
                  <Flex UNSAFE_style={{ paddingRight: "12px" }}>
                    <View UNSAFE_className="call-history-avatar">
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        UNSAFE_className="agent-avatar"
                        UNSAFE_style={{
                          backgroundColor,
                          borderRadius: "50%",
                        }}
                      >
                        <Text UNSAFE_className="call-monitor-agent-text">
                          {initials}
                        </Text>
                      </Flex>
                    </View>
                  </Flex>
                  <Flex
                    width={"100%"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction="column"
                      alignItems="start"
                      UNSAFE_className="call-detail-history-list"
                    >
                      <Text UNSAFE_className="call-history-title">
                        {call.displayName}
                      </Text>
                      <Text
                        UNSAFE_style={{ textAlign: "start", color: "#D7373F" }}
                      >
                        <Flex
                          UNSAFE_className={`call-history-subtitle ${
                            missed ? "call-missed" : "call-attend"
                          }`}
                          alignItems={"center"}
                          justifyContent={"center"}
                          marginTop={5}
                        >
                          {call?.self.missed && common?.tab === "History" ? (
                            <Text marginX={5}>
                              <MissedCallIcon />
                            </Text>
                          ) : (
                            <img
                              src={getCallbackIcon()}
                              className="call-history-icon"
                              alt="Missed call icon"
                            />
                          )}
                          <Text
                            marginX={5}
                            UNSAFE_style={{ whiteSpace: "nowrap" }}
                          >
                            {formattedConnectedTime}
                          </Text>
                        </Flex>
                      </Text>
                    </Flex>
                    {!call?.self.missed && (
                      <Flex direction="column">
                        {formattedDifference !== "0s" && (
                          <Text UNSAFE_className="call-history-time-diff" marginStart={5}>
                            {formattedDifference}
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </div>
          );
        })}
    </View>
  );
};

export default CVHistorySidebar;
